import React, { useState, useMemo } from 'react';
import styles from './CompactDiagnosticList.module.css';
import { ISelectionItem } from '../../../Types/Analyse/ISelectionItem';
import { Category } from '../../Utils/SelectionPane/SelectionPane';
import { ISelectOption } from '../CompactFilters/CompactFilters';
import { inDateRange } from '../CompactFilters/filterUtils';

type CompactDiagnosticListProps = {
  categories: Category<string>[];
  onChange: (category: Category<string>) => void;

  // NEW: controlled search term from parent
  searchTerm: string;

  selectedOrganisations?: ISelectOption[];
  selectedDepartments?: ISelectOption[];
  selectedGroups?: ISelectOption[];
  selectedTemplateTypes?: ISelectOption[];
  /** Develop: filter by completed-for (observee / individual), same semantics as Develop landing */
  selectedObservees?: ISelectOption[];
  /** Develop: filter by owner display name */
  selectedOwners?: ISelectOption[];
  /** Develop: inclusive completion-date bounds as DayKeys ('YYYY-MM-DD'), '' when unset. */
  completedFrom?: string;
  completedTo?: string;
};

const CompactDiagnosticList = ({
  categories,
  onChange,
  searchTerm, // NEW
  selectedOrganisations = [],
  selectedDepartments = [],
  selectedGroups = [],
  selectedTemplateTypes = [],
  selectedObservees = [],
  selectedOwners = [],
  completedFrom = '',
  completedTo = '',
}: CompactDiagnosticListProps) => {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(categories.map((cat) => cat.title))
  );

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };
  const handleItemToggle = (
    category: Category<string>,
    item: ISelectionItem<string>
  ) => {
    // Don't allow toggling disabled items
    if (item.disabled) return;

    const newData = category.data.map((i) =>
      i.id === item.id ? { ...i, selected: !i.selected } : i
    );
    onChange({ ...category, data: newData });
  };

  const handleSelectAll = (category: Category<string>) => {
    const allSelected = category.data
      .filter((item) => !item.disabled)
      .every((item) => item.selected);

    const newData = category.data.map((item) => ({
      ...item,
      selected: item.disabled ? item.selected : !allSelected,
    }));

    onChange({ ...category, data: newData });
  };

  const getSelectedCount = (category: Category<string>): number => {
    return category.data.filter((item) => item.selected).length;
  };

  const getTotalCount = (category: Category<string>): number => {
    return category.data.length;
  };

  const hasEnabledItems = (category: Category<string>): boolean => {
    return category.data.some((item) => !item.disabled);
  };

  // Client-side filtering: compute which items should be disabled based on selected filters
  const categoriesWithClientFiltering = useMemo(() => {
    const hasActiveFilters =
      selectedOrganisations.length > 0 ||
      selectedDepartments.length > 0 ||
      selectedGroups.length > 0 ||
      selectedTemplateTypes.length > 0 ||
      selectedObservees.length > 0 ||
      selectedOwners.length > 0 ||
      !!completedFrom ||
      !!completedTo;

    if (!hasActiveFilters) {
      // No filters applied: avoid cloning every item unless something actually needs re-enabling.
      const anyDisabled = categories.some((category) =>
        category.data.some((item) => item.disabled)
      );
      if (!anyDisabled) {
        return categories;
      }
      return categories.map((category) => ({
        ...category,
        data: category.data.map((item) => ({ ...item, disabled: false })),
      }));
    }

    // Apply client-side filtering
    const selectedOrgNames = new Set(selectedOrganisations.map((o) => o.label));
    const selectedDeptNames = new Set(selectedDepartments.map((d) => d.label));
    const selectedGroupNames = new Set(selectedGroups.map((g) => g.label));
    const selectedTemplateNames = new Set(selectedTemplateTypes.map((t) => t.label));
    const selectedObserveeNames = new Set(selectedObservees.map((o) => o.label));
    const selectedOwnerNames = new Set(selectedOwners.map((o) => o.label));

    return categories.map((category) => {
      const templateTypeMatches = selectedTemplateNames.size === 0 || selectedTemplateNames.has(category.title);

      return {
        ...category,
        data: category.data.map((item) => {
          // Access metadata properties from the backend
          const itemAny = item as any;
          
          // Check if metadata properties exist on the item
          const hasOrgProperty = 'organisations' in itemAny || 'organizations' in itemAny;
          const hasDeptProperty = 'departments' in itemAny;
          const hasGroupProperty = 'groups' in itemAny;
          
          const itemOrgs = itemAny.organisations || itemAny.organizations || [];
          const itemDepts = itemAny.departments || [];
          const itemGroups = itemAny.groups || [];

          let matches = true;

          // Check template type (filter by category title)
          if (!templateTypeMatches) {
            matches = false;
          }

          // Apply filters similar to EvaluateActive's PagedCardCollection
          // Only filter by orgs if the property exists on items
          if (matches && selectedOrgNames.size > 0 && hasOrgProperty) {
            if (!itemOrgs.some((org: string) => selectedOrgNames.has(org))) {
              matches = false;
            }
          }

          // Only filter by departments if the property exists on items
          if (matches && selectedDeptNames.size > 0 && hasDeptProperty) {
            if (!itemDepts.some((dept: string) => selectedDeptNames.has(dept))) {
              matches = false;
            }
          }

          // Only filter by groups if the property exists on items
          if (matches && selectedGroupNames.size > 0 && hasGroupProperty) {
            if (!itemGroups.some((group: string) => selectedGroupNames.has(group))) {
              matches = false;
            }
          }

          // An item has one observee/owner, so multi-select is OR: match ANY
          // selected name. (Matches PagedCardCollection's computeDisabledIds.)
          if (matches && selectedObserveeNames.size > 0) {
            const completedFor = (
              (itemAny.observee || itemAny.individual || '') as string
            ).trim();
            if (!completedFor || !selectedObserveeNames.has(completedFor)) {
              matches = false;
            }
          }

          if (matches && selectedOwnerNames.size > 0) {
            const owner = ((itemAny.ownerName || '') as string).trim();
            if (!owner || !selectedOwnerNames.has(owner)) {
              matches = false;
            }
          }

          // Date Completed - inclusive both ends; an item with no completion
          // date fails as soon as either bound is set.
          if (matches && (completedFrom || completedTo)) {
            if (!inDateRange(itemAny.completedDate, completedFrom, completedTo)) {
              matches = false;
            }
          }

          return {
            ...item,
            disabled: !matches,
          };
        }),
      };
    });
  }, [categories, selectedOrganisations, selectedDepartments, selectedGroups, selectedTemplateTypes, selectedObservees, selectedOwners, completedFrom, completedTo]);

  // Filter categories based on search term only
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) {
      return categoriesWithClientFiltering;
    }

    const searchLower = searchTerm.toLowerCase();

    return categoriesWithClientFiltering
      .filter((category) =>
        category.title.toLowerCase().includes(searchLower)
      )
      .concat(
        categoriesWithClientFiltering.filter((category) =>
          !category.title.toLowerCase().includes(searchLower)
        ).map((category) => ({
          ...category,
          _isSearchMismatch: true
        }))
      );
  }, [categoriesWithClientFiltering, searchTerm]);

  return (
    <div className={styles.container}>
      <div className={styles.categoriesContainer}>
        {(() => {
          // Check if any filters are applied
          const hasActiveFilters = 
            selectedOrganisations.length > 0 || 
            selectedDepartments.length > 0 || 
            selectedGroups.length > 0 || 
            selectedTemplateTypes.length > 0 ||
            selectedObservees.length > 0 ||
            selectedOwners.length > 0;

          // Separate categories into enabled and disabled
          const enabledCategories = filteredCategories.filter(cat => cat.data.some(item => !item.disabled));
          const disabledCategories = filteredCategories.filter(cat => cat.data.every(item => item.disabled));

          const renderCategory = (category: Category<string>) => {
            const selectedCount = getSelectedCount(category);
            const totalCount = getTotalCount(category);
            const allEnabledSelected =
              category.data
                .filter((item) => !item.disabled)
                .every((item) => item.selected) &&
              hasEnabledItems(category);
            const isExpanded = expandedCategories.has(category.title);
            const isSearchMismatch = (category as any)._isSearchMismatch;

            return (
              <div 
                key={category.title}
                className={`${styles.categorySection} ${
                  isSearchMismatch ? styles.categorySearchMismatch : ''
                }`}
              >
                <div className={styles.categoryHeader}>
                  <div className={styles.headerLeft}>
                    <button
                      className={styles.expandButton}
                      onClick={() => toggleCategory(category.title)}
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      <span className={styles.expandIcon}>
                        {isExpanded ? '▼' : '▶'}
                      </span>
                    </button>
                    <h3 className={styles.categoryTitle}>{category.title}</h3>
                    <span className={styles.categoryCount}>
                      {selectedCount} of {totalCount} selected
                    </span>
                  </div>
                  <button
                    className={styles.selectAllButton}
                    onClick={() => handleSelectAll(category)}
                  >
                    {allEnabledSelected ? 'Deselect all' : 'Select all'}
                  </button>
                </div>

                {isExpanded && (
                  <div className={styles.diagnosticList}>
                  {category.data.map((item) => {
                    const isDisabled = item.disabled;
                    const isSelected = item.selected;

                    return (
                      <label title={item.text}
                        key={item.id}
                        className={`${styles.diagnosticItem} ${
                          isDisabled ? styles.disabled : ''
                        } ${isSelected ? styles.selected : ''}`}
                        onClick={(e) => {
                          if (isDisabled) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => handleItemToggle(category, item)}
                          className={styles.checkbox}
                        />
                        <span className={styles.diagnosticText}>{item.text}</span>
                      </label>
                    );
                  })}
                  </div>
                )}
              </div>
            );
          };

          return (
            <>
              {/* Render enabled categories */}
              {enabledCategories.map(renderCategory)}
              
              {/* Add separator only once if there are both enabled and disabled categories */}
              {hasActiveFilters && enabledCategories.length > 0 && disabledCategories.length > 0 && (
                <div className={styles.categorySeparator}></div>
              )}
              
              {/* Render disabled categories */}
              {disabledCategories.map(renderCategory)}
            </>
          );
        })()}
      </div>
    </div>
  );
};

// Cloning/filtering every item is costly; skip re-renders while unrelated parts
// of the Analyse page (chat stream, prompt input) update.
export default React.memo(CompactDiagnosticList);

